import { Box, Stack, StackDivider, VStack } from "@chakra-ui/react";
import { encode } from "js-base64";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactPlayer from "react-player";
import useHomeworkState from "../state/homework.state";
import {
  countOverlaps,
  getElapsedFromStart,
  sortByStartTime,
} from "../utils/common";
import { Cue, cuesToVTT } from "../utils/vtt";
import CueForm from "./forms/CueForm";
import CueItem from "./items/CueItem";

type Props = {
  videoRef: ReactPlayer;
  cues: Cue[];
  setCues: (
    kind: "captions" | "subtitles",
  ) => (setSubtitlesFn: (subtitles: Cue[]) => Cue[]) => void;
  kind: "captions" | "subtitles" | "captions_and_subtitles";
  isDefault?: boolean;
  isDisabled?: boolean;
};

export default function CueManager({
  videoRef,
  cues,
  setCues,
  kind,
  isDefault = true,
  isDisabled = false,
}: Props) {
  const { t } = useTranslation();
  const { categories } = useHomeworkState();
  const [cueToEdit, setCueToEdit] = useState<{
    globalIndex: number;
    index: number;
    type: "captions" | "subtitles";
  } | null>(null);

  const updateCues = useCallback(
    (newVttBase64: string, cueKind: "captions" | "subtitles") => {
      const videoElement = videoRef?.getInternalPlayer();
      if (videoElement instanceof HTMLVideoElement) {
        const newTrack = document.createElement("track");
        newTrack.id = cueKind;
        newTrack.src = `data:text/plain;base64,${newVttBase64}`;
        newTrack.kind = cueKind;
        newTrack.default = isDefault;

        const trackToRemove = videoElement.querySelector(`#${cueKind}`);

        if (trackToRemove) {
          videoElement.removeChild(trackToRemove);
        }

        videoElement?.appendChild(newTrack);
      } else {
        console.warn("Video not supported");
      }
    },
    [isDefault, videoRef],
  );

  useEffect(() => {
    if (kind === "captions_and_subtitles") {
      const { captions, subtitles } = cues.reduce(
        (p, c) => ({ ...p, [c.type]: [...p[c.type], c] }),
        { captions: [], subtitles: [] },
      );
      updateCues(encode(cuesToVTT(captions, categories)), "captions");
      updateCues(encode(cuesToVTT(subtitles, categories)), "subtitles");
    } else {
      updateCues(encode(cuesToVTT(cues, categories)), kind);
    }
  }, [updateCues, cues, categories, kind]);

  const onSubmit = useCallback(
    (cue: Cue) => {
      setCues(cue.type)((oldCues) => {
        let newCues: Cue[] = [];

        if (cueToEdit === null) {
          newCues = [...oldCues, cue];
        } else {
          newCues = oldCues.map((oldCue, index) =>
            index === cueToEdit.index && oldCue.type === cueToEdit.type
              ? cue
              : oldCue,
          );
        }

        return newCues.sort(sortByStartTime);
      });

      setCueToEdit(null);
    },
    [cueToEdit, setCues],
  );

  const onCueClick = useCallback(
    (cue: Cue) => {
      videoRef?.seekTo(getElapsedFromStart(cue.startTime));
    },
    [videoRef],
  );

  const onCueDelete = useCallback(
    (cueIndex: number) => {
      setCues(cues[cueIndex].type)((oldCues) =>
        oldCues.filter((_, index) => index !== cueIndex),
      );
      setCueToEdit(null);
    },
    [cues, setCues],
  );

  const renderCues = useCallback(() => {
    const indexMapping: { [key in "subtitles" | "captions"]: number } = {
      subtitles: -1,
      captions: -1,
    };
    return cues.sort(sortByStartTime).map((cue, index) => {
      indexMapping[cue.type] = indexMapping[cue.type] + 1;
      const cueIndex = indexMapping[cue.type];

      return (
        <CueItem
          key={`${cue.startTime}${cue.endTime}${cue.text}${cueIndex}`}
          cue={cue}
          category={cue.class ? categories[cue.class] : undefined}
          index={index}
          isSelected={
            cueToEdit?.index === cueIndex && cueToEdit.type === cue.type
          }
          isDisabled={isDisabled}
          onClick={() => onCueClick(cue)}
          onDelete={() => onCueDelete(cueIndex)}
          onEdit={() =>
            setCueToEdit({
              index: cueIndex,
              type: cue.type,
              globalIndex: index,
            })
          }
          leaveEdit={() => setCueToEdit(null)}
        />
      );
    });
  }, [
    categories,
    cueToEdit?.index,
    cueToEdit?.type,
    cues,
    isDisabled,
    onCueClick,
    onCueDelete,
  ]);

  const currentEditCue = useMemo(() => {
    const indexMapping: { [key in "subtitles" | "captions"]: number } = {
      subtitles: -1,
      captions: -1,
    };

    for (let i = 0; i < cues.length; i++) {
      indexMapping[cues[i].type] = indexMapping[cues[i].type] + 1;
      const cueIndex = indexMapping[cues[i].type];

      if (cueIndex === cueToEdit?.index && cues[i].type === cueToEdit.type) {
        return cues[i];
      }
    }
  }, [cueToEdit?.index, cueToEdit?.type, cues]);

  const checkOverlaps = useCallback(
    (startTime: string, endTime: string, type: "captions" | "subtitles") => {
      const cuesToCheck = cues.filter(
        (cue, index) =>
          cue.type === type &&
          (cueToEdit === null || cueToEdit.globalIndex !== index),
      );

      if (countOverlaps(startTime, endTime, cuesToCheck) > 1) {
        return t("forms.validation.no_more_overlaps");
      }
    },
    [cueToEdit, cues, t],
  );

  return (
    <Stack divider={<StackDivider borderColor="gray.200" />} gap={4}>
      {!isDisabled && (
        <Box>
          <CueForm
            defaultValue={currentEditCue}
            onSubmit={onSubmit}
            validateCue={checkOverlaps}
            buttonText={
              cueToEdit !== null
                ? `${t("common.buttons.edit")} #${cueToEdit.globalIndex}`
                : t("common.buttons.add")
            }
            isDisabled={isDisabled}
          />
        </Box>
      )}
      <VStack align="stretch" divider={<StackDivider borderColor="gray.200" />}>
        {renderCues()}
      </VStack>
    </Stack>
  );
}
